<template>
  <div class="card card-custom gutter-b" :class="classes">
    <div v-if="hasTitleSlot || title" class="card-header" :class="headClass">
      <div class="card-title">
        <slot name="title" v-if="hasTitleSlot"></slot>
        <h3 class="card-label" v-else>
          {{ title }}
        </h3>
      </div>
      <div v-if="hasToolbarSlot" class="card-toolbar">
        <slot name="toolbar"></slot>
      </div>
    </div>
    <div
      class="card-body"
      v-bind:class="{
        [bodyClass]: true,
        'body-fit': bodyFit,
        'body-fluid': bodyFluid
      }"
    >
      <div v-if="$slots['col1'] && $slots['col2']" class="d-flex">
        <div class="card-column">
          <slot name="col1" />
        </div>
        <div class="card-column">
          <slot name="col2" />
        </div>
      </div>
      <slot v-else name="body">
        <p class="mb-0">{{ text }}</p>
      </slot>
    </div>
    <div class="card-footer" v-if="hasFootSlot">
      <slot name="foot"></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: "KTCard",
  props: {
    /**
     * Title of the card
     */
    title: String,
    /**
     * Text content of the card
     */
    text: String,
    /**
     * Set card head size. Eg. md, lg, sm, etc.
     */
    headSize: String,
    /**
     * Set card to fluid
     */
    fluidHeight: Boolean,
    /**
     * Set card to fluid in half
     */
    fluidHalfHeight: Boolean,
    /**
     * Set overlay head
     */
    headOverlay: Boolean,
    /**
     * Set extra class for main card
     */
    cardClass: String,
    /**
     * Set extra class for card head
     */
    headClass: String,
    /**
     * Set extra class for card body
     */
    bodyClass: String,
    /**
     * Set card body to fit
     */
    bodyFit: Boolean,
    /**
     * Set card body to fluid
     */
    bodyFluid: Boolean,
    /**
     * Code examples
     */
    example: Boolean,
    /**
     * Adds box-shadow
     */
    shaded: {
      type: Boolean,
      default: true
    },
    /**
     * Add border around card
     */
    border: Boolean,
    /**
     * Shows a thick border in variant color and no background color
     */
    outline: Boolean,
    /**
     * Stretch card vertically
     */
    stretch: Boolean,
    /**
     * Give card a background color
     */
    variant: {
      type: String,
      default: ""
    }
  },
  components: {},
  methods: {},
  computed: {
    classes() {
      const cls = {
        "example example-compact": this.example,
        "height-fluid": this.fluidHeight,
        "height-fluid-half": this.fluidHalfHeight,
        "head-overlay": this.headOverlay,
        shaded: this.shaded,
        bordered: this.border,
        outline: this.outline,
        "card-stretch": this.stretch,
        "has-columns": this.$slots["col1"] && this.$slots["col2"]
      };

      cls[this.headSizeClass] = this.headSizeClass;

      // append extra classes
      if (this.cardClass) {
        cls[this.cardClass] = true;
      }

      if (this.variant) {
        cls[`card-variant-${this.variant}`] = true;
      }

      return cls;
    },
    hasTitleSlot() {
      return !!this.$slots["title"];
    },
    hasToolbarSlot() {
      return !!this.$slots["toolbar"];
    },
    hasFootSlot() {
      return !!this.$slots["foot"];
    },
    headSizeClass() {
      if (this.headSize) {
        return `head-${this.headSize}`;
      }
      return false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/assets/sass/variables/main";

.bordered {
  border: 1px solid $border-color;
}
.outline {
  border: 2px solid $border-color;
  background-color: transparent !important;
}

.has-columns .card-body::after {
  content: "";
  height: 100%;
  width: 1px;
  background-color: rgba(0, 0, 0, 0.1);
  left: 50%;
  top: 0;
  position: absolute;
}
.has-columns .card-column {
  width: 50%;
  padding: 0 2.25rem;
  &:first-child {
    padding-left: 0;
  }
  &:last-child {
    padding-right: 0;
  }
}

.card-variant-info {
  background-color: lighten($info, 45%);
  &.outline {
    border: 2px solid $info;
  }
  .card-header {
    border-bottom: 1px solid rgba(0,0,0,0.15);
  }
}
.card-variant-success {
  background-color: lighten($success, 25%);
  &.outline {
    border: 2px solid $success;
  }
  .card-header {
    border-bottom: 1px solid rgba(0,0,0,0.15);
  }
}
.card-variant-warning {
  &.outline {
    border: 2px solid $warning;
  }
  background-color: lighten($warning, 40%);
}
</style>